// finance helper object
/* common parameters:
*
* rate - interest rate per payment period
* pmt  - payment amount (per period)
* nper - number of periods (months)
* pv   - present value
* fv   - future value
* type - when the payments are due:
*        0: end of the period, e.g. end of month (default)
*        1: beginning of period
*/

exports.PMT = function (rate, nper, pv, fv = 0, type = 0) {
  if (rate === 0) {
    return -(pv + fv) / nper
  }
  const pvif = Math.pow(1 + rate, nper)
  return -rate * (pv * pvif + fv) / ((1 + rate * type) * (pvif - 1))
}

exports.PV = function (rate, nper, pmt, fv = 0, type = 0) {
  if (rate === 0) {
    return -(fv + pmt * nper)
  }
  const pvif = Math.pow(1 + rate, nper)
  return ((pmt * (1 + rate * type) * (1 - pvif) / rate) - fv) / pvif
}

exports.FV = function (rate, nper, pmt, pv = 0, type = 0) {
  if (rate === 0) {
    return -(pv + pmt * nper)
  }
  const pvif = Math.pow(1 + rate, nper)
  return (pmt * (1 + rate * type) * (1 - pvif) / rate) - pv * pvif
}

exports.NPER = function (rate, pmt, pv, fv = 0, type = 0) {
  if (rate === 0) {
    return -(fv + pv) / pmt
  }
  const pmtAdj = pmt * (1 + type * rate)
  return Math.log((pmtAdj - fv * rate) / (pmtAdj + pv * rate)) / Math.log(1.0 + rate)
}

exports.RATE_N = function (nper, pmt, pv, fv = 0, type = 0, guess = 0.01) {
  // no longer the default implementation because it fails on a number of test cases
  // from https://gist.github.com/kucukharf/677d8f21660efaa33f72
  var epsMax = 1e-10 // Set maximum epsilon for end of iteration
  var iterMax = 20 // Set maximum number of iterations
  // Implement Newton's method
  var y; var y0; var y1; var x0; var x1; var i
  var pvif = 0
  var rate = guess
  if (Math.abs(rate) < epsMax) {
    y = pv * (1 + nper * rate) + pmt * (1 + rate * type) * nper + fv
  } else {
    pvif = Math.exp(nper * Math.log(1 + rate))
    y = pv * pvif + pmt * (1 / rate + type) * (pvif - 1) + fv
  }
  y0 = pv + pmt * nper + fv
  y1 = pv * pvif + pmt * (1 / rate + type) * (pvif - 1) + fv
  i = x0 = 0
  x1 = rate
  while ((Math.abs(y0 - y1) > epsMax) && (i < iterMax)) {
    rate = (y1 * x0 - y0 * x1) / (y1 - y0)
    x0 = x1
    x1 = rate
    if (Math.abs(rate) < epsMax) {
      y = pv * (1 + nper * rate) + pmt * (1 + rate * type) * nper + fv
    } else {
      pvif = Math.exp(nper * Math.log(1 + rate))
      y = pv * pvif + pmt * (1 / rate + type) * (pvif - 1) + fv
    }
    y0 = y1
    y1 = y
    ++i
  }
  if (i >= iterMax) {
    return null
  }
  if (Number.isNaN(y)) { // apparent bug or otherwise failure in this implementation of Newton's method!
    rate = exports.RATE(nper, pmt, pv, fv, type, guess) // fall back to simple iterative method
  }
  return rate
}

exports.RATE = function (nper, pmt, pv, fv, type, guess) {
  // started with https://gist.github.com/1FahadShakeel/2ca9e61efc604ded5336b959d7ea4baf
  // (which purports to have fixed some number of bugs found in the original)
  // and then fixed several bugs with it...
  // Sets default values for missing parameters
  fv = typeof fv !== 'undefined' ? fv : 0;
  type = typeof type !== 'undefined' ? type : 0;
  guess = typeof guess !== 'undefined' ? guess : 0.1;

  // Sets the limits for possible guesses to any
  // number between 0% and 100%
  var lowLimit = 0;
  var highLimit = 1;

  // Defines a tolerance of up to +/- 0.00005% of pmt, to accept
  // the solution as valid.
  const tolerance = Math.abs(0.00000005 * pmt);
  const iterMax = 40 // Set maximum number of iterations
  const npv = pv < 0 // negative present value
  const nrt = Math.abs(nper * pmt) < Math.abs(pv) // negative rate
  if (nrt) {
    if (guess > 0) {
      guess = -guess;
    }
    highLimit = -highLimit;
  }

  // Tries at most iterMax times to find a solution within the tolerance.
  for (var i = 0; i < iterMax; i++) {
      // Resets the balance to the original pv.
      var balance = pv;

      // Calculates the balance at the end of the loan, based
      // on loan conditions.
      for (var j = 0; j < nper; j++ ) {
          if (type === 0) {
              // Interests applied before payment
              balance = balance * (1 + guess) + pmt;
          } else {
              // Payments applied before insterests
              balance = (balance + pmt) * (1 + guess);
          }
      }

      // Returns the guess if balance is within tolerance.  If not, adjusts
      // the limits and starts with a new guess.
      if (Math.abs(balance + fv) < tolerance) {
          return guess;
      } else if ((npv !== nrt) ? balance + fv < 0 : balance + fv > 0) {
          // Sets a new highLimit knowing that
          // the current guess was too big.
          highLimit = guess;
      } else  {
          // Sets a new lowLimit knowing that
          // the current guess was too small.
          lowLimit = guess;
      }

      // Calculates the new guess.
      guess = (highLimit + lowLimit) / 2;
  }
  // Returns null if no acceptable result was found after iterMax tries.
  return null;
}
